import { useCallback, useRef, useState } from "react";

/**
 * Bir form gönderimi için sabit kalan idempotency anahtarı üretir.
 *
 * <p>Backend, ödeme / borç / tahsilat uçlarında `Idempotency-Key` başlığını
 * kabul ediyor ve aynı anahtarla gelen ikinci isteği yeni bir işlem olarak
 * DEĞİL, ilkinin tekrarı olarak görüyor. Anahtar gönderilmezse koruma hiç
 * devreye girmiyor — bu yüzden gönderen taraf burası.
 *
 * <p><b>Anahtarın ömrü kritik:</b> form açıldığı andan gönderim BAŞARIYLA
 * tamamlanana kadar aynı kalmalı.
 * <ul>
 *   <li>Her istekte yeni anahtar üretilseydi çift tıklama iki farklı anahtar
 *       taşır ve koruma hiç çalışmazdı.</li>
 *   <li>Başarıdan sonra sıfırlanmasaydı kullanıcı ikinci bir kayıt giremez,
 *       ilk kaydın kopyasını geri alırdı.</li>
 * </ul>
 * Bu yüzden başarıda {@link reset} çağrılır. Hata durumunda karar tek yerde
 * veriliyor: `describeAxiosError` / `describeFetchError` dönen `shouldResetKey`
 * alanı (bkz. utils/apiErrors.js). Özeti: sunucu isteği görüp REDDETTİYSE kayıt
 * oluşmamıştır ve kullanıcı formu düzeltip göndereceği için anahtar tazelenir;
 * ağ hatası / 503 / 409 durumunda istek sunucuda sürüyor ya da ulaşmış olabilir,
 * anahtar KORUNUR — tam da korumanın işe yarayacağı an burasıdır.
 *
 * <p>Gönderim anında {@code key} yerine {@code keyRef.current} okunur: reset'ten
 * sonraki render'a kadar {@code key} eski değeri taşır, ref ise anında tazedir.
 *
 * @example
 * const idem = useIdempotencyKey();
 * await api.post(url, body, { headers: { "Idempotency-Key": idem.keyRef.current } });
 * idem.reset(); // başarıda; hatada yalnız detail.shouldResetKey ise
 */
const newKey = () => {
    // crypto.randomUUID güvenli bağlamlarda (https ve localhost) mevcut.
    // Eski tarayıcı / http kaynak durumunda sessizce çökmemesi için yedek üretim.
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

export const useIdempotencyKey = () => {
    const [key, setKey] = useState(newKey);

    // Aynı render içinde reset sonrası okunan değerin taze olması için ref.
    const keyRef = useRef(key);
    keyRef.current = key;

    const reset = useCallback(() => {
        const next = newKey();
        keyRef.current = next;
        setKey(next);
    }, []);

    return { key, keyRef, reset };
};

export default useIdempotencyKey;
